import { useCallback, useMemo, useState } from 'react';
import { BantQualification, BantSortKey, BantFilter } from '../types';
import * as bantService from '../services/bant.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseBantProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageBant: boolean;
}

export function useBant({ accessToken, refreshToken, attemptRefresh, canManageBant }: UseBantProps) {
  // State
  const [qualifications, setQualifications] = useState<BantQualification[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedQualification, setSelectedQualification] = useState<BantQualification | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<BantFilter>('all');
  const [sort, setSort] = useState<{ column: BantSortKey; direction: 'asc' | 'desc' }>({
    column: 'createdAt',
    direction: 'desc'
  });

  // Pagination state
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  // Filtered and sorted qualifications
  const filteredQualifications = useMemo(() => {
    // Ensure qualifications is always an array
    if (!Array.isArray(qualifications)) {
      return [];
    }
    let result = qualifications;

    // Apply filter
    if (filter === 'qualified') {
      result = result.filter((q) => q.qualificado === 'Sim');
    } else if (filter === 'not_qualified') {
      result = result.filter((q) => q.qualificado === 'Nao' || q.qualificado !== 'Sim');
    }

    // Apply search
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      result = result.filter((q) => {
        return (
          q.chat.nome.toLowerCase().includes(searchLower) ||
          q.chat.telefone.includes(searchLower) ||
          q.chat.chatId.includes(searchLower) ||
          (q.observacoes && q.observacoes.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply sort
    return [...result].sort((a, b) => {
      let comparison = 0;

      switch (sort.column) {
        case 'nome':
          comparison = a.chat.nome.localeCompare(b.chat.nome);
          break;
        case 'score':
          comparison = a.score - b.score;
          break;
        case 'qualificado':
          comparison = a.qualificado.localeCompare(b.qualificado);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }

      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [qualifications, filter, search, sort]);

  // Toggle sort
  const toggleSort = useCallback((column: BantSortKey) => {
    setSort((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Fetch qualifications
  const fetchQualifications = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canManageBant) return { success: false, error: 'Nao autorizado' };

    setLoading(true);

    try {
      const response = await withAuthRetry(
        () => bantService.fetchBantQualifications(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setQualifications(Array.isArray(response.data) ? response.data : []);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
      setCurrentPage(response.currentPage);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar qualificacoes BANT'
      };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canManageBant]);

  // Fetch single qualification by chatId
  const fetchQualificationByChatId = useCallback(
    async (chatId: string): Promise<{ success: boolean; error?: string; data?: BantQualification }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        const data = await withAuthRetry(
          () => bantService.fetchBantQualificationByChatId(chatId, accessToken!),
          refreshToken,
          attemptRefresh
        );
        return { success: true, data };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao buscar qualificacao BANT'
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  // Delete qualification
  const deleteQualification = useCallback(
    async (chatId: string): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setDeleting(true);

      try {
        await withAuthRetry(
          () => bantService.deleteBantQualification(chatId, accessToken!),
          refreshToken,
          attemptRefresh
        );
        // Remove from local state
        setQualifications((prev) => prev.filter((q) => q.chat.chatId !== chatId));
        setDeleteDialogOpen(false);
        setSelectedQualification(null);
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao deletar qualificacao BANT'
        };
      } finally {
        setDeleting(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  // Open details dialog
  const openDetails = useCallback((qualification: BantQualification) => {
    setSelectedQualification(qualification);
    setDetailsOpen(true);
  }, []);

  // Open delete dialog
  const openDeleteDialog = useCallback((qualification: BantQualification) => {
    setSelectedQualification(qualification);
    setDeleteDialogOpen(true);
  }, []);

  return {
    // State
    qualifications: filteredQualifications,
    loading,
    selectedQualification,
    detailsOpen,
    setDetailsOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deleting,
    search,
    setSearch,
    filter,
    setFilter,
    sort,
    toggleSort,
    totalElements,
    totalPages,
    currentPage,

    // Actions
    fetchQualifications,
    fetchQualificationByChatId,
    deleteQualification,
    openDetails,
    openDeleteDialog
  };
}
